import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { deepCopy, getOptionsQueryParams } from "@app/class/Utilities";
import { IComboBox } from "@app/interfaces/IComboBox";
import { IGroupUserMapping } from "@app/interfaces/IUser";
import { IVehicle } from "@app/interfaces/IVehicle";
import { AddGroupRequest } from "@app/main/components/resourceManagement/groups/_models/add-group-request";
import { IGroupWithIds } from "@app/main/components/resourceManagement/groups/_models/IGroupWithIds";
import { UpdateGroupRequest } from "@app/main/components/resourceManagement/groups/_models/update-group-request";
import { firstValueFrom, forkJoin, from, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { IGroup } from "../../main/components/resourceManagement/groups/_models/IGroup";
import { DataCacheService } from "../caching/data-cache.service";
import { TranslationService } from "../translation.service";
import { UserMappingsService } from "../userMappings.service";

@Injectable({
    providedIn: 'root'
})
export class GroupService {
    private readonly groupsWithIdsCache = "groupsWithIds";
    private readonly groupsBasicCache = "groupsBasic";
    
    constructor(
        private readonly http: HttpClient,
        private readonly dataCacheService: DataCacheService,
        private readonly userMappingsService: UserMappingsService,
        private readonly translationService: TranslationService
    ) { }

    addGroupsIdsToVehicles(vehicles: IVehicle[] = [], groups: IGroupWithIds[] = []): void {
        if (!vehicles?.length || !groups?.length) {
            return;
        }

        vehicles.forEach(vehicle => {
            vehicle.GroupIds = groups.filter(group => group.VehicleIds.includes(vehicle.Id)).map(group => group.Id);
        });
    }

    findGroup(groups: IGroupWithIds[], groupId: number): IGroupWithIds {
        return groups.find(x => x.Id === groupId);
    }

    hasDriver(group: IGroupWithIds, driverId: number): boolean {
        if (group.DriverIds.includes(driverId)) {
            return true;
        };

        if (group.children && group.children.length > 0) {
            for (let i = 0; i < group.children.length; i++) {
                if (this.hasDriver(group.children[i], driverId)) {
                    return true;
                }
            }
        }

        return false;
    }

    hasVehicle(group: IGroupWithIds, vehicleId: number): boolean {
        if (group.VehicleIds.includes(vehicleId)) {
            return true;
        }
    
        return group.children?.some(child => this.hasVehicle(child, vehicleId)) ?? false;
    }

    findGroupIdsAssetBelongsTo(groups: IGroupWithIds[], idToMatch: number): number[] {
        return groups
            .filter(group => group.VehicleIds.includes(idToMatch))
            .map(group => group.Id);
    }

    findDepotsWithHomeLocations(groups: IGroupWithIds[]): IGroupWithIds[] {
        return groups.filter(g => g.IsDepot && g.GeofenceId);
    }

    getGroupComboBox(groups: IGroup[]): IComboBox[] {
        const options: IComboBox[] = [
            { Name: this.translationService.t("groupService.all"), Value: 0 }
        ];
    
        const groupOptions = groups.map(group => ({
            Name: group.Name,
            Value: group.Id
        }));
    
        return [...options, ...groupOptions];
    }

    async getGroup(id: number): Promise<IGroup> {
        const request = this.http.get<IGroup>(`/api/groups/${id}`);
        return await firstValueFrom(request);
    }

    async groupExists(groupName: string, existingGroupId: number): Promise<boolean> {
        const params = getOptionsQueryParams({ groupName, existingGroupId });
        const request = this.http.get<boolean>(`/api/groups/groupexists`, params);

        return await firstValueFrom(request);
    }

    async getGroups(invalidateCache?: boolean): Promise<IGroup[]> {
        const request = () => this.getGroupsFromDb();

        const cacheRequest = this.dataCacheService.getOrRequest(
            this.groupsBasicCache, 
            request, 
            { invalidateCache }
        ).pipe(map((groups: IGroup[]) => {
            return [...groups];
        }));

        return await firstValueFrom(cacheRequest);
    }

    async getGroupsHierarchy(invalidateCache?: boolean): Promise<IGroupWithIds[]> {
        const groups = await this.getGroupsWithIds(invalidateCache);
        return this.buildGroupHierarchy(groups);
    }

    async getGroupsWithIds(invalidateCache?: boolean): Promise<IGroupWithIds[]> {
        const request = () => 
            forkJoin({
                groups: this.getGroupsWithIdsFromDb(),
                usersMappings: from(this.userMappingsService.getGroupMappings() as Promise<IGroupUserMapping[]>)
            }).pipe(map(data => {
                return data.groups.map(g => {
                    return {...g, userIds: this.getUserIdsForGroups(g, data.usersMappings) }
                })
            }))
            

        const cacheRequest = this.dataCacheService.getOrRequest(
            this.groupsWithIdsCache, 
            request, 
            { invalidateCache }
        ).pipe(map((groups: IGroupWithIds[]) => {
            return deepCopy(groups);
        }));

        return await firstValueFrom(cacheRequest);
    }

    private getUserIdsForGroups(group: IGroupWithIds, mappings: IGroupUserMapping[]): number[] {
        return mappings.filter(x => x.GroupId === group.Id).map(x => x.UserId);
    }

    async addGroup(data: AddGroupRequest): Promise<number> {
        const request = this.http.post<number>("/api/groups/add", data);
        return await firstValueFrom(request);
    }

    async updateGroup(data: UpdateGroupRequest): Promise<void> {
        const request = this.http.post<void>("/api/groups/update", data);
        await firstValueFrom(request);
    }

    async deleteGroup(group: IGroupWithIds): Promise<void> {
        const request = this.http.delete<void>(`/api/groups/${group.Id}`);

        await firstValueFrom(request);
    }

    getNewGroupWithIds(): IGroupWithIds {
        return {
            Id: -1,
            AccountId: -1,
            Name: "",
            FuelRateTarget: 0,
            IsDepot: false, 
            VehicleIds: [],
            DriverIds: [],
            GeofenceIds: [],

            children: [],
            userIds: []
        }
    }

    sumParents(group: IGroupWithIds, acc = 1): number {
        if (!group) return 0;

        return !group.parent ? acc : this.sumParents(group.parent, ++acc);
    }

    buildGroupHierarchy(groups: IGroupWithIds[], useSameArray: boolean = false): IGroupWithIds[] {
        if (groups.length === 0) {
            return [];
        }

        // Hierarchy already built
        if (groups[0].hasOwnProperty('children' as keyof IGroupWithIds)) {
            return groups;
        }

        const cleanGroups = useSameArray ? groups : this.copyAndClearGroups(groups);

        if (cleanGroups.length === 1) {
            return cleanGroups;
        }

        const rootGroups = cleanGroups.filter(x => !x.ParentId || !cleanGroups.map(x => x.Id).includes(x.ParentId));
        rootGroups.sort((a, b) => a.Name.toLowerCase().localeCompare(b.Name.toLowerCase()));

        const hierarchy = rootGroups.map(x => this.buildHierarchyRecursively(x, cleanGroups));

        return hierarchy;
    }

    private copyAndClearGroups(groups: IGroupWithIds[]): IGroupWithIds[] {
        return groups.map(g => {
            return { ...g, parent: null, children: [] }
        });
    }

    private buildHierarchyRecursively(parent: IGroupWithIds, groups: IGroupWithIds[]): IGroupWithIds {
        parent.children = groups.filter(x => x.ParentId === parent.Id);

        if (parent.ParentId) {
            parent.parent = groups.find(x => x.Id === parent.ParentId);
        }
    
        for (const child of parent.children) {
            this.buildHierarchyRecursively(child, groups);
        }
    
        return parent;
    }

    private gatherAllGroupsForCacheRemoval(group: IGroup, groups: IGroup[], listForRemoval: IGroup[]): IGroup[] {
        const childGroups = groups.filter(g => g.ParentId === group.Id);

        childGroups.forEach(g => {
            listForRemoval.push(g);
            this.gatherAllGroupsForCacheRemoval(g, groups, listForRemoval);
        });

        return listForRemoval;
    }

    private getGroupsWithIdsFromDb(): Observable<IGroupWithIds[]> {
        return this.http.get<IGroupWithIds[]>("/api/groups/withIds");   
    }

    private getGroupsFromDb(): Observable<IGroup[]> {
        return this.http.get<IGroup[]>("/api/groups");   
    }
}
